import React from 'react';
import { useRef } from 'react';
import { Box, Typography, Grid, Button, useTheme, TextField} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ExamInput, Question, Result, SectionType, VideoLink } from './type';
import MainButton from '../Courses/MainButton';
import { BorderColor } from '@mui/icons-material';
import QuestionComponent from './QuestionComponent';

interface QuestionListProps {
  sections: SectionType[];
  isMobile: boolean;
  activeQuestion: Question | null;
  check: boolean;
  showSingleQuestion: (question: Question) => void;
  result?: Result;
  showResultAnswer?: boolean;
  isCreateMode?: boolean;

  questions: Question[];
    viewAnswer?: boolean;
    showResult?: boolean;
    formatSize?: number;
    isExamming?: boolean;
    setResult?: (result: Result) => void;
    listLinks?: VideoLink[];
    loadingSubmit?: boolean;
    handleSubmit?: () => void;
    pdfLink?: string;
      setExamInput?: React.Dispatch<React.SetStateAction<ExamInput>>;
    
}

// Helper function to get and format the answer from the result
const getFormattedAnswer = (question: Question, result: Result | undefined) => {
  const answerFound = result?.answers.find(
    (answer) => answer.questionId === question.questionId
  );

  const userAnswer = answerFound
    ? (answerFound?.answer as string)
    : question.correctAnswer;

  if (question.type === 'mctf' && userAnswer) {
    return userAnswer
      .split('') // Split the answer into individual characters
      .map((char) => (char === '1' ? 'Đ' : char === '0' ? 'S' : '_')) // Map '1' to 'Đ', '0' to 'S'
      .join(''); // Join the array back into a string
  }

  return userAnswer || ''; // Return the formatted answer or empty string
};

// Function to format the answer
const formatAnswer = (
  questionIndex: number,
  question: Question,
  result: Result | undefined,
  showResultAnswer: boolean
) => {
  if (!showResultAnswer) return `${questionIndex + 1}. `; // Return the question number only if not checking
  const formattedAnswer = getFormattedAnswer(question, result);
  return `${questionIndex + 1}. ${formattedAnswer}`;
};

// Function to check if the answer is correct
const checkCorrect = (question: Question, result: Result | undefined) => {
  if (!result) return 0;
  const answerFound = result?.answers.find(
    (answer) => answer.questionId === question.questionId
  );

  if (answerFound && !answerFound.check) {
    return 0;
  }

  const userAnswer = answerFound
    ? (answerFound?.answer as string)
    : question.correctAnswer;

  //if (userAnswer.length === 0) return 0; // No answer provided

  const correctAnswer = question.correctAnswer;

  return userAnswer === correctAnswer ? 1 : -1; // 1 for correct, -1 for incorrect
};

const muiInputSx = {
  width: 36,
  '& .MuiOutlinedInput-root': {
    height: 36,
    fontSize: 14,
    textAlign: 'center',

    // Ẩn border
    '& fieldset': {
      border: 'none',
    },

    // Không hiện border khi hover / focus
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
  '& input': {
    textAlign: 'center',
    padding: 0,
  },
};




const QuestionButtonList: React.FC<QuestionListProps> = ({
  sections,
  isMobile,
  activeQuestion,
  check = false,
  showSingleQuestion,
  result,
  showResultAnswer = true,
  isCreateMode = false,
  questions,
  viewAnswer = false,
  showResult = false,
  formatSize,
  isExamming,
  setResult,
  setExamInput,
  listLinks,
  loadingSubmit,
  handleSubmit,
  pdfLink,
}) => {
  const theme = useTheme();
const inputRefs = useRef<HTMLInputElement[]>([]);

console.log('Rendering QuestionButtonList, isCreateMode:', isCreateMode);


const renderCreateMode = () => {
  let globalIndex = 0;

  function handleInputChange(question: Question, correctAnswer: string) {
  setExamInput?.((prev) => {
    if (!prev) return prev;

    const updatedQuestions = prev.questions.map((q) =>
      q.questionId === question.questionId
        ? { ...q, correctAnswer }
        : q
    );

    return {
      ...prev,
      questions: updatedQuestions,
      isEdittingQuestion: true,
    };
  });
}

  return (
    <Box>
      {sections.map((section, sectionIndex) => (
        <Box key={sectionIndex} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            {section.title}
          </Typography>

          <Grid container spacing={1.5}>
            {section.questions.map((q, idx) => {
              const inputCount = q.type === 'sa' ? 4 : 1;
              const baseIndex = globalIndex;
              globalIndex += inputCount;

              return (
                <Grid
                  item
                  xs={q.type === 'mcq' ? 4 : q.type === 'mctf' ? 6 : 12}
                  key={q.questionId}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      px: 1,
                      py: 0.75,
                      bgcolor: 'none',
                    }}
                  >
                    <Typography sx={{ width: 28, textAlign: 'right' }}>
                      {idx + 1}.
                    </Typography>

                    {/* MCQ */}
                    {q.type === 'mcq' && (
                      <TextField
                        inputRef={(el) => {
                          if (el) inputRefs.current[baseIndex] = el;
                        }}
                        variant="outlined"
                        size="small"
                        defaultValue={q.correctAnswer || ''}
                        sx={muiInputSx}
                        inputProps={{ maxLength: 1 }}
                        onChange={(e) => {
                          const v = e.target.value.toUpperCase();
                          if (!/^[ABCD]?$/.test(v)) return;
                          e.target.value = v;
                          if (v)
                            inputRefs.current[baseIndex + 1]?.focus();
                          handleInputChange(q, v);
                        }}
                      />
                    )}

                    {/* MCTF */}
                    {q.type === 'mctf' && (
                      <TextField
                        inputRef={(el) => {
                          if (el) inputRefs.current[baseIndex] = el;
                        }}
                        variant="outlined"
                        size="small"
                        defaultValue={q.correctAnswer.replace(/1/g, 'D').replace(/0/g, 'S') || ''}
                        sx={{ ...muiInputSx, width: 72 }}
                        inputProps={{ maxLength: 4 }}
                        onChange={(e) => {
                          const v = e.target.value.toUpperCase();
                          if (!/^[DS]{0,4}$/.test(v)) return;
                          e.target.value = v;
                          if (v.length === 4)
                            inputRefs.current[baseIndex + 1]?.focus();
                          handleInputChange(q, v);
                        }}
                      />
                    )}

                    {/* SA */}
                    {q.type === 'sa' && (
                      <Box display="flex" gap={0.75}>
                        {[0, 1, 2, 3].map((i) => (
                          <TextField
                            key={i}
                            inputRef={(el) => {
                              if (el)
                                inputRefs.current[baseIndex + i] = el;
                            }}
                            variant="outlined"
                            size="small"
                            defaultValue={q.correctAnswer?.[i] ?? ''}
                            sx={muiInputSx}
                            inputProps={{
                              maxLength: 1,
                              inputMode: 'numeric',
                            }}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (!/^[0-9\s\-,]?$/.test(v)) return;
                              e.target.value = v;

                              if (v)
                                inputRefs.current[baseIndex + i + 1]?.focus();
                              
                              handleInputChange(q, 
                                [
                                  ...Array(4).keys()
                                ]
                                  .map((j) => (
                                    inputRefs.current[baseIndex + j]?.value
                                  ))  .join('')
                              );  
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};




const renderStudentMode = () => (
  <Box>
    {/* {sections.map((section, sectionIndex) => (
      <Box key={sectionIndex} sx={{ mb: 2 }}>
        <Typography variant="subtitle2">
          {section.title}
        </Typography>

        <Grid container spacing={1}>
          {section.questions.map((q, idx) => (
            <Grid item xs={12} key={q.questionId}>
              <Button
                variant="outlined"
                sx={{
                  width: '100%',
                  borderRadius: 4,
                  background: check
                    ? checkCorrect(q, result) === 1
                      ? alpha(theme.palette.success.main, 0.4)
                      : checkCorrect(q, result) === -1
                      ? alpha(theme.palette.error.main, 0.4)
                      : 'none'
                    : 'none',
                }}
                onClick={() => showSingleQuestion(q)}
              >
                {formatAnswer(idx, q, result, showResultAnswer)}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>
    ))} */}

    {sections.map((section, sectionIndex) => (
      <Box key={sectionIndex} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          {section.title}
        </Typography>

        <Grid container spacing={1}>
          {section.questions.map((question, index) => (
            <div
          key={index}
          // ref={(el) => (questionRefs.current[index] = el)} // Assign refs dynamically
        >
          {isExamming ? (
            result && (
              <QuestionComponent
                question={question}
                answer={
                  result.answers.find(
                    (answer) => answer.questionId === question.questionId
                  )?.answer
                }
                editable={true}
                onAnswerChange={(answer) => {
                  const index = questions.indexOf(question);
                  const newAnswers = [...result.answers];
                  newAnswers[index].answer = answer;
                  if (
                    (question.type === 'mcq' && answer.length > 0) ||
                    (question.type === 'mctf' &&
                      answer.length > 0 &&
                      !answer.includes('_'))
                  ) {
                    newAnswers[index].check = true;
                  }
                  setResult && setResult({ ...result, answers: newAnswers });
                }}
                formatSize={formatSize}
                checkCorrect={false}
              />
            )
          ) : (
            <QuestionComponent
              key={index}
              question={
                viewAnswer
                  ? question
                  : {
                      ...question,
                      correctAnswer: showResult ? question.correctAnswer : '',
                      explain: '',
                      explainImageUrl: '',
                    }
              }
              editable={false}
              answer={
                showResult
                  ? result?.answers.find(
                      (answer) => answer.questionId === question.questionId
                    )?.answer
                  : undefined
              }
              checkCorrect={
                showResult
                  ? result?.answers.find(
                      (answer) => answer.questionId === question.questionId
                    )?.check
                  : undefined
              }
              formatSize={formatSize}
            />
          )}
        </div>
          ))}
        </Grid>
      </Box>
    ))}
  </Box>
);

{/* {questions.map((question, index) => (
        <div
          key={index}
          ref={(el) => (questionRefs.current[index] = el)} // Assign refs dynamically
        >
          {isExamming ? (
            result && (
              <QuestionComponent
                question={question}
                answer={
                  result.answers.find(
                    (answer) => answer.questionId === question.questionId
                  )?.answer
                }
                editable={true}
                onAnswerChange={(answer) => {
                  const index = questions.indexOf(question);
                  const newAnswers = [...result.answers];
                  newAnswers[index].answer = answer;
                  if (
                    (question.type === 'mcq' && answer.length > 0) ||
                    (question.type === 'mctf' &&
                      answer.length > 0 &&
                      !answer.includes('_'))
                  ) {
                    newAnswers[index].check = true;
                  }
                  setResult && setResult({ ...result, answers: newAnswers });
                }}
                formatSize={formatSize}
                checkCorrect={false}
              />
            )
          ) : (
            <QuestionComponent
              key={index}
              question={
                viewAnswer
                  ? question
                  : {
                      ...question,
                      correctAnswer: showResult ? question.correctAnswer : '',
                      explain: '',
                      explainImageUrl: '',
                    }
              }
              editable={false}
              answer={
                showResult
                  ? result?.answers.find(
                      (answer) => answer.questionId === question.questionId
                    )?.answer
                  : undefined
              }
              checkCorrect={
                showResult
                  ? result?.answers.find(
                      (answer) => answer.questionId === question.questionId
                    )?.check
                  : undefined
              }
              formatSize={formatSize}
            />
          )}
        </div>
      ))}
       */}

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
      }}
    >
      {/* <Box>
        {sections.map((section, sectionIndex) => (
          <Box
            sx={{ my: 1 }}
            key={`section-${sectionIndex}`} // Unique key for section
          >
            <Typography variant="subtitle2" gutterBottom>
              {section.title}
            </Typography>
            <Grid container spacing={isMobile ? 1 : 0.5}>
              {section.questions.map((question, questionIndex) => (
                <Grid
                  item
                  xs={question.type === 'mcq' ? 4 : 6}
                  key={question.questionId}
                >
                  <Button
                    variant="outlined"
                    sx={{
                      width: '100%',
                      borderRadius: 4,
                      // show green button if correct, pink button if incorrect
                      background: check
                        ? checkCorrect(question, result) === 0
                          ? 'none'
                          : checkCorrect(question, result) === 1
                          ? alpha(theme.palette.success.main, 0.5)
                          : alpha(theme.palette.error.main, 0.5)
                        : 'none',
                    }}
                    key={`question-${sectionIndex}-${questionIndex}`} // Unique key for each question
                    onClick={() => {
                      showSingleQuestion(question);
                    }}
                  >
                    {formatAnswer(
                      questionIndex,
                      question,
                      result,
                      showResultAnswer
                    )}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Box> */}
      {isCreateMode ? renderCreateMode() : renderStudentMode()}
    </Box>
  );
};

export default QuestionButtonList;
